import { FC, useMemo } from "react";
import { useLocation, useNavigate } from "react-router-dom";
import Warning from "../../UI/Icons/Warning";
import styles from "./TimeSheetAlert.module.scss";

interface TimeSheetAlertProps {
  totalPending: number;
  totalOverdue?: number;
  goToLink: string;
}

const TimeSheetAlert: FC<TimeSheetAlertProps> = ({ 
  totalPending, 
  totalOverdue = 0, 
  goToLink 
}) => {
  const location = useLocation();
  const navigate = useNavigate();

  // Calculate next Monday and deadline status
  const { nextMonday, isDeadlineToday, timezone } = useMemo(() => {
    const now = new Date();
    const currentDay = now.getDay(); // 0 = Sunday, 1 = Monday, etc.
    
    // Determine if we're in AEDT or AEST
    // Australia uses daylight saving from first Sunday in October to first Sunday in April
    const isDaylightSaving = (): boolean => {
      const month = now.getMonth(); // 0-11
      
      // AEDT period: October to March (next year)
      if (month >= 9 || month <= 2) { // Oct, Nov, Dec, Jan, Feb, Mar
        return true;
      }
      return false;
    };
    
    const tz = isDaylightSaving() ? "AEDT" : "AEST";
    
    // Calculate next Monday (or today if it's Monday before 5pm)
    let deadlineDate: Date;
    let isTodayDeadline = false;
    
    if (currentDay === 1) {
      // It's Monday - deadline is today
      deadlineDate = new Date(now);
      isTodayDeadline = true;
    } else if (currentDay === 0) {
      // Sunday - next Monday is tomorrow
      deadlineDate = new Date(now);
      deadlineDate.setDate(now.getDate() + 1);
    } else {
      // Tuesday to Saturday - calculate days until next Monday
      const daysUntilMonday = 8 - currentDay;
      deadlineDate = new Date(now);
      deadlineDate.setDate(now.getDate() + daysUntilMonday);
    }
    
    return {
      nextMonday: deadlineDate,
      isDeadlineToday: isTodayDeadline,
      timezone: tz,
    };
  }, []);

  // Format date as "Mon 22 Dec 2025"
  const formatDeadlineDate = (date: Date): string => {
    const options: Intl.DateTimeFormatOptions = {
      weekday: "short",
      day: "numeric",
      month: "short",
      year: "numeric",
    };
    return date.toLocaleDateString("en-AU", options).replace(",", "");
  };

  // Check if current route is Payroll Pending page
  const isPayrollPendingPage = location.pathname.includes("/payroll-pending");

  // Don't show link on Payroll Pending page
  const showLink = !isPayrollPendingPage;

  const handleLinkClick = () => {
    navigate(goToLink);
  };

  // Determine if there are overdue tasks
  const hasOverdueTasks = totalOverdue > 0;
  
  // If no pending tasks at all, don't show alert
  if (totalPending === 0 && totalOverdue === 0) {
    return null;
  }

  // Determine alert state: overdue (red) or deadline today (red) or normal (info)
  const isAlertDanger = hasOverdueTasks || isDeadlineToday;
  
  const alertClassName = isAlertDanger
    ? `${styles["timesheet-alert"]} ${styles["timesheet-alert--danger"]}`
    : `${styles["timesheet-alert"]} ${styles["timesheet-alert--info"]}`;

  // Build alert message
  const getAlertMessage = () => {
    if (hasOverdueTasks) {
      // Overdue state
      const count = totalOverdue;
      const taskWord = count === 1 ? "payroll task" : "payroll tasks";
      return (
        <>
          There {count === 1 ? "is" : "are"} {count} overdue {taskWord}. 
          Please ensure this is processed ASAP.
        </>
      );
    } else {
      // Normal pending state
      const count = totalPending;
      const taskWord = count === 1 ? "payroll task" : "payroll tasks";
      return (
        <>
          There {count === 1 ? "is" : "are"} <strong>{count}</strong> {taskWord} pending. 
          Please ensure this is processed by{" "}
          {isDeadlineToday ? (
            <strong>today at 5pm {timezone}</strong>
          ) : (
            <>
              {formatDeadlineDate(nextMonday)} at 5pm {timezone}
            </>
          )}
          .
        </>
      );
    }
  };

  return (
    <div className={alertClassName} style={{ width: 'fit-content' }}>
      <div className="d-flex align-items-center gap-10">
      <Warning />
      <span className="fs-13 fw-400 text-black">
        {getAlertMessage()}{" "}
        {showLink && (
        <button
          type="button"
          onClick={handleLinkClick}
          className={`${styles["timesheet-alert__link"]} fw-700`}
        >
          Go to Payroll Pending
        </button>
        )}
      </span>
      </div>
    </div>
  );
};

export default TimeSheetAlert;
